/**
 * English, German and French display names for material and finish samples.
 *
 * Sample names are stored in Dutch ("3M IJ20-10R (monomeer wit)") and were
 * copied unchanged into the en/de/fr translations. This translates the Dutch
 * descriptive words at render time. Brand and series names (Orajet, Forex,
 * Dibond, Invercote, Gmund Cotton, ...) are not in the dictionary and stay as
 * they are. Longer phrases are listed first so word order can be adapted.
 */
type Locale = "en" | "de" | "fr";
type Entry = [nl: string, en: string, de: string, fr: string];

const PHRASES: Entry[] = [
  // full phrases with their own word order
  ["non-woven vliesbehang", "non-woven wallpaper", "Vliestapete", "papier peint intissé"],
  ["contoursnijden met rillijnen", "contour cutting with crease lines", "Konturschnitt mit Rilllinien", "découpe à la forme avec rainage"],
  ["hoogglans uv-lak all over", "high-gloss UV varnish all over", "Hochglanz-UV-Lack vollflächig", "vernis UV brillant intégral"],
  ["hoogglans uv-lak spot", "high-gloss spot UV varnish", "Hochglanz-Spot-UV-Lack", "vernis UV sélectif brillant"],
  ["eenzijdig gestreken sulfaatkarton", "SBS board coated on one side", "einseitig gestrichener Sulfatkarton", "carton sulfaté couché une face"],
  ["tweezijdig gestreken sulfaatkarton", "SBS board coated on both sides", "beidseitig gestrichener Sulfatkarton", "carton sulfaté couché deux faces"],
  ["ongestreken premium hoogwit", "premium uncoated high white", "Premium ungestrichen hochweiß", "non couché premium blanc intense"],
  ["mat gestreken premium", "premium matt coated", "Premium matt gestrichen", "couché mat premium"],
  ["hv offset natuurkarton", "natural offset board", "Offset-Naturkarton", "carton offset naturel"],
  ["hvo bankpost wit", "white bond paper", "weißes Bankpostpapier", "papier bouffant blanc"],
  ["hvo bankpost", "bond paper", "Bankpostpapier", "papier bouffant"],
  ["hv offset", "offset paper", "Offsetpapier", "papier offset"],
  ["hvo offset", "offset paper", "Offsetpapier", "papier offset"],
  ["gesatineerd mc", "satin coated paper", "seidenmatt gestrichenes Papier", "papier couché satiné"],
  ["aluminium composiet", "aluminium composite", "Aluminium-Verbundplatte", "composite aluminium"],
  ["geborsteld aluminium", "brushed aluminium", "gebürstetes Aluminium", "aluminium brossé"],
  ["hpl met folieprint", "HPL with printed film", "HPL mit Foliendruck", "HPL avec film imprimé"],
  ["helder plexiglas", "clear Plexiglas", "klares Plexiglas", "Plexiglas transparent"],
  ["gerecycled forex®", "recycled Forex®", "recyceltes Forex®", "Forex® recyclé"],
  ["gerecycled forex","recycled Forex", "recyceltes Forex", "Forex recyclé"],
  ["flexibel pvc", "flexible PVC", "flexibles PVC", "PVC souple"],
  ["holografische folie", "holographic film", "holografische Folie", "film holographique"],
  ["one way vision folie", "one way vision film", "One-Way-Vision-Folie", "film one way vision"],
  ["clearview folie", "clear view film", "Clearview-Folie", "film clear view"],
  ["glasheldere folie", "crystal clear film", "glasklare Folie", "film transparent cristal"],
  ["zuignapjesfolie transparant", "transparent suction cup film", "transparente Saugnapffolie", "film à micro-ventouses transparent"],
  ["zuignapjesfolie wit", "white suction cup film", "weiße Saugnapffolie", "film à micro-ventouses blanc"],
  ["zuignapjesfolie", "suction cup film", "Saugnapffolie", "film à micro-ventouses"],
  ["neon papier geel", "neon yellow paper", "Neonpapier gelb", "papier fluo jaune"],
  ["neonpapier geel", "neon yellow paper", "Neonpapier gelb", "papier fluo jaune"],
  ["kraft papier", "kraft paper", "Kraftpapier", "papier kraft"],
  ["papier glans", "gloss paper", "Glanzpapier", "papier brillant"],
  ["papier mat", "matt paper", "Mattpapier", "papier mat"],
  ["antiek wit", "antique white", "Antikweiß", "blanc antique"],
  ["polymeer wit", "polymeric white", "polymer weiß", "polymère blanc"],
  ["polymeer transparant", "polymeric transparent", "polymer transparent", "polymère transparent"],
  ["monomeer wit", "monomeric white", "monomer weiß", "monomère blanc"],
  ["monomeer transparant", "monomeric transparent", "monomer transparent", "monomère transparent"],
  ["gegoten wit", "cast white", "gegossen weiß", "coulé blanc"],
  ["pvc-vrij wit", "PVC-free white", "PVC-frei weiß", "blanc sans PVC"],
  ["wit gecoat", "white coated", "weiß beschichtet", "revêtement blanc"],
  ["wit glans", "white gloss", "weiß glänzend", "blanc brillant"],
  ["wit matte", "white matt", "weiß matt", "blanc mat"],
  ["wit mat", "white matt", "weiß matt", "blanc mat"],
  ["mat/mat", "matt/matt", "matt/matt", "mat/mat"],
  ["scheurvast, waterproof", "tear-resistant, waterproof", "reißfest, wasserfest", "indéchirable, étanche"],
  ["dot folie", "DOT film", "DOT-Folie", "film DOT"],
  ["recycle-look", "recycled look", "Recycling-Optik", "aspect recyclé"],
  ["foliedruk", "foil print", "Folienveredelung", "dorure"],
  ["hotfoil", "hot foil", "Heißfolie", "dorure à chaud"],
  // single words
  ["beukenhout", "beech wood", "Buchenholz", "bois de hêtre"],
  ["zandstraalfolie", "sandblast film", "Sandstrahlfolie", "film sablé"],
  ["melkglasfolie", "frosted glass film", "Milchglasfolie", "film verre dépoli"],
  ["vloerfolie", "floor film", "Bodenfolie", "film de sol"],
  ["vliesbehang", "non-woven wallpaper", "Vliestapete", "papier peint intissé"],
  ["textielbehang", "textile wallpaper", "Textiltapete", "papier peint textile"],
  ["dekostof", "decor fabric", "Dekostoff", "tissu déco"],
  ["sulfaatkarton", "SBS board", "Sulfatkarton", "carton sulfaté"],
  ["natuurkarton", "natural board", "Naturkarton", "carton naturel"],
  ["linnenpers", "linen embossed", "Leinenprägung", "gaufrage lin"],
  ["gehamerd", "hammered", "gehämmert", "martelé"],
  ["gerecycled", "recycled", "recycelt", "recyclé"],
  ["geborsteld", "brushed", "gebürstet", "brossé"],
  ["horizontaal", "horizontal", "horizontal", "horizontal"],
  ["verticaal", "vertical", "vertikal", "vertical"],
  ["gestreken", "coated", "gestrichen", "couché"],
  ["ongestreken", "uncoated", "ungestrichen", "non couché"],
  ["polystyreen", "polystyrene", "Polystyrol", "polystyrène"],
  ["polymeer", "polymeric", "polymer", "polymère"],
  ["monomeer", "monomeric", "monomer", "monomère"],
  ["gegoten", "cast", "gegossen", "coulé"],
  ["pvc-vrij", "PVC-free", "PVC-frei", "sans PVC"],
  ["transparant", "transparent", "transparent", "transparent"],
  ["helder", "clear", "klar", "transparent"],
  ["opaal", "opal", "opal", "opale"],
  ["hoogglans", "high gloss", "Hochglanz", "brillant"],
  ["zijdeglans", "silk gloss", "Seidenglanz", "satiné"],
  ["glans", "gloss", "glänzend", "brillant"],
  ["matte", "matt", "matt", "mat"],
  ["mat", "matt", "matt", "mat"],
  ["parelmoer", "pearl", "Perlmutt", "nacré"],
  ["roségoud", "rose gold", "Roségold", "or rose"],
  ["goud", "gold", "Gold", "or"],
  ["zilver", "silver", "Silber", "argent"],
  ["koper", "copper", "Kupfer", "cuivre"],
  ["regenboog", "rainbow", "Regenbogen", "arc-en-ciel"],
  ["zwart", "black", "schwarz", "noir"],
  ["wit", "white", "weiß", "blanc"],
  ["geel", "yellow", "gelb", "jaune"],
  ["rood", "red", "rot", "rouge"],
  ["blauw", "blue", "blau", "bleu"],
  ["groen", "green", "grün", "vert"],
  ["grijs", "grey", "grau", "gris"],
  ["hout", "wood", "Holz", "bois"],
  ["folie", "film", "Folie", "film"],
  ["papier", "paper", "Papier", "papier"],
  ["karton", "board", "Karton", "carton"],
  ["met", "with", "mit", "avec"],
];

const COLUMN: Record<Locale, number> = { en: 1, de: 2, fr: 3 };
const LETTER = "A-Za-zÀ-ÖØ-öø-ÿ0-9";
const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\/]/g, "\\$&");
const COMPILED = PHRASES.map((entry) => ({
  entry,
  pattern: new RegExp(`(?<![${LETTER}-])${escape(entry[0])}(?![${LETTER}-])`, "gi"),
}));

/**
 * Translates the Dutch words in a sample name. Every match is replaced in one
 * pass over the original text, so a translated word is never translated again.
 */
export function translateSampleName(name: string, locale: string): string {
  if (!name) return name || "";
  if (!(locale in COLUMN)) return name;
  const column = COLUMN[locale as Locale];
  const taken: { start: number; end: number; text: string }[] = [];
  for (const { entry, pattern } of COMPILED) {
    pattern.lastIndex = 0;
    for (const m of name.matchAll(pattern)) {
      const start = m.index ?? 0;
      const end = start + m[0].length;
      if (taken.some((t) => start < t.end && end > t.start)) continue;
      taken.push({ start, end, text: entry[column] });
    }
  }
  if (!taken.length) return name;
  taken.sort((a, b) => a.start - b.start);
  let out = "";
  let cursor = 0;
  for (const t of taken) {
    out += name.slice(cursor, t.start) + t.text;
    cursor = t.end;
  }
  out += name.slice(cursor);
  return out.charAt(0).toUpperCase() + out.slice(1);
}
